import {
  BondPriceComponent,
  CouponPaymentComponent,
  MaturityDateComponent,
  MinimumInvestmentComponent,
  OutstandingAmountComponent,
  YieldComponent,
} from "@goodfill/domain-properties-components";
import { Button, CardRow, Field, Flex, Grid, CardSectionCompact, Card, CardHeader } from "@goodfill/ui-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { route } from "../../../routes";
import { FavorBond } from "../detail/FavorBond";
import { PricedBond } from "../PricedBond";
import "./styles/BondCardCompact.scss";
import { iconUrlByTicker } from "../../../assets/tickerAssetsIndex";
import { TIMEZONE_NEY_YORK } from "../../../locale/timezone";

interface Props {
  item: PricedBond;
  showButtons?: boolean;
  style?: React.CSSProperties;
}

export const BondCardCompact = ({ item, showButtons, style }: Props) => {
  const [detailsButtonIsHighlighted, setDetailsButtonIsHighlighted] = useState(true);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const onMouseEnterDetailsButton = () => {
    setDetailsButtonIsHighlighted(true);
  };
  const onMouseLeaveDetailsButton = () => {
    setDetailsButtonIsHighlighted(false);
  };

  const navigateToDetails = () => {
    navigate(`/bonds/${item.bondIdCusip}`);
  };

  const navigateToInvest = () => {
    navigate(route.orderBuy(item.bondIdCusip));
  };

  const iconUrl = iconUrlByTicker(item.issuerTicker);

  return (
    <Card className='card-max-width' style={style}>
      <CardHeader wide={false}>
        <>
          <Flex grow={1} direction="row" items="center" justify="between">
            {iconUrl ? <img src={iconUrl} alt="" className="max-w-[40px] max-h-[40px] w-[40px]" /> : null}
            <Flex>{item.issuerTicker}</Flex>
          </Flex>
        </>
      </CardHeader>
      <CardSectionCompact>
        <div>
          <CardRow justify="around">
            <Field label={t("bond.issuerName")} textAlign="center">
              <Flex>{item.issuerName}</Flex>
            </Field>
            <FavorBond pricedBond={item} />
          </CardRow>
        </div>
        <div>
          <Grid cols="grid-cols-2-50p">
            <Field label={t("bond.couponPayment")} textAlign="center">
              <CouponPaymentComponent value={item.couponPayment} />
            </Field>
            <Field label={t("bond.maturityDate")} textAlign="center">
              <MaturityDateComponent value={item.maturityDate} timezone={TIMEZONE_NEY_YORK} />
            </Field>
          </Grid>
        </div>

        <div>
          <Grid cols="grid-cols-2-50p">
            <Field label={t("bond.offerYield")} textAlign="center">
              <YieldComponent value={item.buyOfferYield} />
            </Field>
            <Field label={t("bond.offerPrice")} textAlign="center">
              <BondPriceComponent value={item.askPrice} />
            </Field>
          </Grid>
        </div>

        <div>
          <Field label={t("bond.minimumPiece")} textAlign="center">
            <MinimumInvestmentComponent value={item.minimumPiece.amount} />
          </Field>
        </div>

        <div>
          <Field label={t("bond.outstandingAmount")} textAlign="center">
            <OutstandingAmountComponent value={item.outstandingAmount.amount} />
          </Field>
        </div>

        <div>
          <Grid cols="grid-cols-2-50p">
            <Field label={t("bond.ratings.S&P")} textAlign="center">
              {item.ratings.sp ?? ""}
            </Field>
            <Field label={t("bond.ratings.Moody")} textAlign="center">
              {item.ratings.moodys ?? ""}
            </Field>
          </Grid>
        </div>
      </CardSectionCompact>
      {showButtons !== false ? (
        <Grid cols="grid-cols-2-50p">
          <div className="card-buttons-div">
            <Button
              fill
              id={`invest-${item.bondIdCusip}`}
              data-testid={`invest-${item.bondIdCusip}`}
              primaryGradient={true}
              font="medium"
              uppercase={false}
              size="large"
              onClick={navigateToInvest}
            >
              {t("callToAction.invest")}
            </Button>
          </div>
          <div className="card-buttons-div">
            <Button
              fill
              data-testid={`details-${item.bondIdCusip}`}
              primary={detailsButtonIsHighlighted}
              outlined={detailsButtonIsHighlighted}
              onMouseEnter={onMouseEnterDetailsButton}
              onMouseLeave={onMouseLeaveDetailsButton}
              font="medium"
              uppercase={false}
              size="large"
              onClick={navigateToDetails}
            >
              {t("callToAction.details")}
            </Button>
          </div>
        </Grid>
      ) : null}
    </Card>
  );
};
